import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Input} from 'components';
import DropdownList from './DropdownList';
import _ from 'lodash';

const SearchableDropdown = ({
  suggestions = [],
  onQueryChange,
  onSelect
}) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const allSuggestions = _.flatMap(suggestions, section => section.suggestionViewModels.toJS())

  useEffect(() => {
    if (!_.isEmpty(allSuggestions) && selectedIndex === -1) {
      setSelectedIndex(0);
    } else if (_.isEmpty(allSuggestions)) {
      setSelectedIndex(-1);
    }
  }, [allSuggestions.length, selectedIndex]);

  useEffect(() => {
    setIsOpenWithTimeout(suggestions && query.length > 0)
  }, [suggestions, query]);

  const setIsOpenWithTimeout = useCallback(open => {
    if (open) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsOpen(true);
    } else {
      if (closeTimeoutRef.current) return;

      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 400)
    }
  }, []);

  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    setSelectedIndex(-1);
    setIsOpen(suggestions && value.length > 0)
    if (onQueryChange) {
      onQueryChange(value);
    }
  }, [onQueryChange]);

  const handleClear = useCallback(() => {
    setQuery('');
    setSelectedIndex(-1);
    if (onQueryChange) {
      onQueryChange('');
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onQueryChange]);

  const handleSelect = useCallback((suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    }
    setIsOpenWithTimeout(false);
  }, [onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen || _.isEmpty(allSuggestions)) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < allSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : allSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelect(allSuggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpenWithTimeout(false);
        break;
      default:
        break;
    }
  }, [isOpen, allSuggestions, selectedIndex, handleSelect]);

  const handleClose = useCallback(() => {
    setIsOpenWithTimeout(false);
  }, []);

  return (
    <div className='searchable-dropdown'>
      <div className='full-height relative'>
        <Input
          type='search'
          value={query}
          inputRef={inputRef}
          onChange={e => handleQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className='searchable-dropdown-input wl-searchable-dropdown'
          placeholder='global_search.Search'
        />
        {query.length > 0 && (
          <i onClick={handleClear} className='searchable-dropdown-close material-icons clickable h3 waterloo absolute' >{'cancel'}</i>
        )}
      </div>
      {isOpen && (
        <DropdownList
          sections={suggestions}
          selectedIndex={selectedIndex}
          query={query}
          onClose={handleClose}
          onSelect={handleSelect}
        />
      )}
    </div>
  );
};

export default SearchableDropdown;
